import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import Ajv from 'ajv';
import { isString } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { reportErrorToBugsnag } from '../../../../src/util/errorReporting/directReporting';

const StreamingMessageSchema = {
  type: 'object',
  required: ['type', 'responseId', 'payload'],
  properties: {
    type: {
      type: 'string',
      enum: ['chunk', 'done', 'error', 'status-update', 'status-update-chunk'],
    },
    responseId: { type: 'string' },
    payload: {
      oneOf: [
        { type: 'string' },
        {
          type: 'object',
          required: ['message', 'metadata'],
          properties: {
            message: { type: 'string' },
            metadata: {
              type: 'object',
              required: ['type'],
              properties: {
                type: { type: 'string' },
              },
              additionalProperties: true,
            },
          },
          additionalProperties: true,
        },
      ],
    },
  },
  additionalProperties: false,
} as const;

type StreamingMessageType =
  | 'chunk'
  | 'done'
  | 'error'
  | 'status-update'
  | 'status-update-chunk';

type StreamingMessageRaw = {
  type: StreamingMessageType;
  responseId: string;
  payload:
    | string
    | {
        message: string;
        metadata: { type: StreamingMessageType; [key: string]: unknown };
      };
};

class StreamingMessage {
  constructor(
    public type: StreamingMessageType,
    public payload: string,
    public metadata: Record<string, unknown>,
    public responseId: string,
  ) {}

  get isDone(): boolean {
    return this.type === 'done' || this.type === 'status-update-chunk';
  }

  static fromRaw(raw: StreamingMessageRaw): StreamingMessage {
    if (isString(raw.payload)) {
      return new StreamingMessage(raw.type, raw.payload, {}, raw.responseId);
    } else {
      return new StreamingMessage(
        raw.type,
        raw.payload.message,
        raw.payload.metadata,
        raw.responseId,
      );
    }
  }
}

export class TypewriterMessage extends StreamingMessage {
  constructor(
    type: StreamingMessageType,
    payload: string,
    metadata: Record<string, unknown>,
    responseId: string,
    public startedAt: number,
  ) {
    super(type, payload, metadata, responseId);
  }

  static fromStreamingMessage(message: StreamingMessage): TypewriterMessage {
    return new TypewriterMessage(
      message.type,
      message.payload,
      message.metadata,
      message.responseId,
      Date.now(),
    );
  }
}

// milliseconds per character
// average human reading speed is 120-240 WPM, which is about 12.5-25 characters per second
// or about 80-40ms per character. so lets "type" faster than that, but not too fast.
const TYPING_SPEED = 15;

export type TypewriterSnapshot =
  | { kind: 'empty' }
  | { kind: 'active'; messages: TypewriterMessage[] };

/** Visible for tests: maps buffered chunks to typewriter display and handles expiry when done. */
export function computeTypewriterSnapshot(
  messages: TypewriterMessage[],
  now: number,
  expireMessage: (responseId: string, delay?: number) => void | (() => void),
): TypewriterSnapshot {
  if (messages.length === 0) {
    return { kind: 'empty' };
  }
  return {
    kind: 'active',
    messages: messages.map(message => {
      const fullPayload = message.payload as string;
      const toShow = fullPayload.slice(
        0,
        Math.floor((now - message.startedAt) / TYPING_SPEED),
      );
      const done = message.isDone && toShow.length === fullPayload.length;
      if (done) {
        expireMessage(message.responseId);
      }
      return new TypewriterMessage(
        message.type,
        toShow,
        message.metadata,
        message.responseId,
        message.startedAt,
      );
    }),
  };
}

export const parseStreamingMessage = (
  message: string,
): StreamingMessage | null => {
  try {
    const parsed = JSON.parse(message) as unknown;
    const validate = new Ajv().compile(StreamingMessageSchema);
    if (!validate(parsed)) {
      console.error('Invalid streaming message', validate.errors);
      return null;
    }
    return StreamingMessage.fromRaw(parsed as StreamingMessageRaw);
  } catch (error) {
    console.error('Invalid streaming message', error);
    return null;
  }
};

/**
 * Takes a topic and returns a connection and the messages being streamed for that topic.
 * Buffers the the messages being streamed into a typewriter effect, where the characters
 * are typed out in a "reading" speed that is natural.
 *
 * Handles reconnecting to the streaming service when the connection is lost,
 * and when the streaming service sends a done message, it expires the message
 * from streaming, so that it can be replaced by its canonical message.
 */
export const useStreamingConnection = (topic: string) => {
  const [ticket, setTicket] = useState<{
    ticketId: string;
    expiresIn: number;
  } | null>(null);

  const [connection, setConnection] = useState<WebSocket | null>(null);
  const expiringMessages = useRef(new Set<string>());

  const { mutate: getTicket } = apiHooks.useAPIMutation(
    'POST /v1/streaming/stream',
    {
      onSuccess: data => {
        setTicket(data as { ticketId: string; expiresIn: number });
      },
    },
  );

  useEffect(() => {
    if (!ticket) {
      getTicket({
        topic,
      });
    }
  }, [topic, getTicket, ticket]);

  const expireMessage = useCallback(
    (responseId: string, delay: number = 10_000) => {
      if (expiringMessages.current.has(responseId)) {
        return;
      } else {
        expiringMessages.current.add(responseId);
      }
      // remove all ephemeral messages that are children of the given responseId
      setMessages(previousMessages =>
        previousMessages.filter(
          m =>
            m.metadata.type !== 'ephemeral' &&
            m.metadata.parent_id !== responseId,
        ),
      );
      const timeout = setTimeout(() => {
        setMessages(previousMessages => {
          return previousMessages.filter(m => m.responseId !== responseId);
        });
        expiringMessages.current.delete(responseId);
      }, delay);
      return () => clearTimeout(timeout);
    },
    [],
  );

  const [messages, setMessages] = useState<TypewriterMessage[]>([]);
  const [typewriterMessages, setTypewriterMessages] = useState<
    TypewriterMessage[]
  >([]);
  useEffect(() => {
    const snapshot = computeTypewriterSnapshot(
      messages,
      Date.now(),
      expireMessage,
    );
    if (snapshot.kind === 'empty') {
      setTypewriterMessages([]);
      return;
    }

    setTypewriterMessages(snapshot.messages);
    const interval = setInterval(() => {
      const next = computeTypewriterSnapshot(
        messages,
        Date.now(),
        expireMessage,
      );
      if (next.kind === 'active') {
        setTypewriterMessages(next.messages);
      }
    }, TYPING_SPEED);

    return () => {
      clearInterval(interval);
    };
  }, [messages, expireMessage]);

  const auth = useServiceAuth();

  useEffect(() => {
    if (!ticket) return;

    // Replace the API base URL with the streaming base URL
    // consider moving this to app config, but this is the only place it's used
    const streamingUrl = `${auth.config.apiBaseUri?.replace(
      /^https:\/\/api\./,
      'wss://streaming.',
    )}?ticketId=${ticket.ticketId}`;

    const ws = new WebSocket(streamingUrl);

    ws.onmessage = (event: MessageEvent<string>) => {
      const message = parseStreamingMessage(event.data);
      if (!message) {
        return;
      }
      if (message.type === 'error') {
        expireMessage(message.responseId, 0);
        return;
      }

      // we _replace_ the existing message with the latest chunk,
      // but we keep the startedAt from previous, so that the typing effect continues
      // and we update the done status if we're done
      // when the typing effect is done _and_ we've received the done message,
      // we expire the message from streaming, so that it can be replaced by its canonical
      // message
      setMessages(previousMessages => {
        const previous = previousMessages.find(
          m => m.responseId === message.responseId,
        );
        const others = previousMessages.filter(
          m => m.responseId !== message.responseId,
        );
        const payload =
          message.isDone && previous?.payload
            ? previous.payload
            : message.payload;
        const startedAt = previous?.startedAt || Date.now();
        return [
          ...others,
          new TypewriterMessage(
            message.type,
            payload,
            message.metadata,
            message.responseId,
            startedAt,
          ),
        ];
      });
    };
    ws.onerror = event => {
      reportErrorToBugsnag(
        new Error('WebSocket error', {
          cause: event,
        }),
      );
      // remove the dead connection
      setConnection(null);
      // clear the ticket, to trigger the useEffect to get a new ticket
      setTicket(null);
    };
    ws.onclose = () => {
      // remove the dead connection
      setConnection(null);
      // clear the ticket, to trigger the useEffect to get a new ticket
      setTicket(null);
    };
    setConnection(ws);

    return () => {
      ws.close();
    };
  }, [ticket, expireMessage, auth.config.apiBaseUri]);

  return { connection, messages: typewriterMessages };
};
